// Deadline radar — bell icon + dropdown panel in the protected chrome.
//
// Sources merged & de-duplicated server-side via GET /api/v1/deadlines.
// The endpoint caches 300s in KV so this can refetch liberally without
// query-storming D1. We refetch on dropdown open + on a 5-minute window
// so the badge stays current.
//
// A11y:
//   - Bell button has aria-expanded + aria-label that includes count
//   - Dropdown is role="dialog" with aria-modal=true
//   - Esc closes; focus returns to the bell button
//   - Click outside closes

import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface DeadlineRow {
  grant_program_id: string;
  grant_title: string;
  funder_name: string;
  funder_level: string;
  closes_at: number;
  source: 'match' | 'project_match' | 'application';
  partner_name: string | null;
  project_name: string | null;
}

const DAYS = 14;
const REFETCH_INTERVAL_MS = 5 * 60 * 1000;

export function DeadlineRadar() {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const deadlines = useQuery({
    queryKey: ['deadlines', DAYS],
    queryFn: () => api.get<DeadlineRow[]>(`/deadlines?days=${DAYS}`),
    refetchInterval: REFETCH_INTERVAL_MS,
    // Fresh on open: matches the KV-cached server side, no spam.
    staleTime: 60_000,
  });

  const rows = deadlines.data?.data ?? [];
  const count = rows.length;
  const buttonLabel = count === 0
    ? `${DAYS}-day deadline radar — no grants closing soon`
    : `${DAYS}-day deadline radar — ${count} grant${count === 1 ? '' : 's'} closing in next ${DAYS} days`;

  // Esc closes; click outside closes; focus returns to button on close.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    function onClick(e: MouseEvent) {
      if (!panelRef.current || !buttonRef.current) return;
      const t = e.target as Node;
      if (panelRef.current.contains(t) || buttonRef.current.contains(t)) return;
      setOpen(false);
    }
    // Refetch on open so the badge reflects whatever happened in the last minute.
    deadlines.refetch();
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={buttonLabel}
        className="relative rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50 flex items-center"
      >
        <BellIcon className="w-4 h-4 text-neutral-700" />
        {count > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-medium bg-accent text-white"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label="Deadline radar"
          className="absolute right-0 top-full mt-1 w-[380px] bg-white border border-neutral-200 rounded-md shadow-lg z-50"
        >
          <header className="px-3 py-2 border-b border-neutral-200">
            <h3 className="text-sm font-semibold text-neutral-900">Closing in next {DAYS} days</h3>
            <p className="text-[11px] text-neutral-500 mt-0.5">
              Verified grants on your watched matches, active projects, and in-progress applications. Refreshes every 5 minutes.
            </p>
          </header>
          <div className="max-h-[480px] overflow-y-auto">
            {deadlines.isLoading && <p className="px-3 py-3 text-xs text-neutral-600">Loading deadlines — a moment.</p>}
            {!deadlines.isLoading && rows.length === 0 && (
              <p className="px-3 py-4 text-xs text-neutral-700">
                No grants closing in the next {DAYS} days. Triage adds more as new matches surface.
              </p>
            )}
            <ul>
              {rows.map((r) => <DeadlineRow key={r.grant_program_id} row={r} onPick={() => setOpen(false)} />)}
            </ul>
          </div>
          <footer className="px-3 py-2 border-t border-neutral-200 bg-neutral-50/40 text-[11px]">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="text-accent underline"
            >View all in Triage →</Link>
          </footer>
        </div>
      )}
    </div>
  );
}

function DeadlineRow({ row, onPick }: { row: DeadlineRow; onPick: () => void }) {
  const days = Math.round((row.closes_at - Date.now()) / 86_400_000);
  const tone = days < 0 ? 'past' : days <= 7 ? 'red' : days <= 14 ? 'amber' : 'neutral';
  const toneCls =
    tone === 'red' ? 'bg-rose-100 text-rose-900' :
    tone === 'amber' ? 'bg-amber-100 text-amber-900' :
    tone === 'past' ? 'bg-neutral-100 text-neutral-500 line-through' :
    'bg-neutral-100 text-neutral-700';

  const caption = row.project_name
    ? `for ${row.project_name}`
    : row.partner_name
      ? `for ${row.partner_name}`
      : '';

  return (
    <li className="border-b border-neutral-100 last:border-0">
      <Link
        to="/"
        onClick={onPick}
        className="block px-3 py-2 hover:bg-neutral-50"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-neutral-900 truncate flex-1">{row.grant_title}</span>
          <span className={'shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ' + toneCls}>
            {days < 0 ? `${Math.abs(days)}d past` : days === 0 ? 'today' : `${days}d`}
          </span>
        </div>
        <div className="text-[11px] text-neutral-600 mt-0.5 truncate">
          {row.funder_name} <span className="uppercase tracking-wide">· {row.funder_level}</span>
          {caption && <> · <span className="text-neutral-700">{caption}</span></>}
        </div>
      </Link>
    </li>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
